import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { graphItemContainerClass, height, subHeaderClass, yAxisLabelClass } from './PVMAnalysisDrilldownContainer';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';

export const legendData = [
    { label: 'Positive', color: colorConstants.limeGreen },
    { label: 'Negative', color: colorConstants.red },
];

import {
    calculateFontSize,
    formatNumWithKMB,
    getAdditionalDataOnCopyClick,
    getConfig,
    getIntersectionOnApplyFilter,
    scaleMaxValue,
    scaleMinValue,
    capitalizeFirstLetterOfEachWord,
    formatNumWithKMBAlt,
} from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PVM_ANALYSIS_ORDER_BY_VALUE_CLICKED,
    PVM_ANALYSIS_CATEGORY_DROP_DOWN_CLICKED,
    PVM_ANALYSIS_DOWNLOAD_DATA,
    PVM_ANALYSIS_COPY_SCREENSHOT,
    PVM_ANALYSIS_DRILL_DOWN_DIM_VALUE_SELECTED,
    PVM_ANALYSIS_ABSOLUTE_PERCENTAGE_TOGGLE_CLICKED,
} from 'utils/mixpanelEventNames';
import Barchart from 'components/molecules/Barchart';
import { getPVMTableDataColumns } from 'utils/pvmAnalysisColumns';
import { useFetchFeatureTableData } from 'hooks/pvmAnalysis/useFetchFeatureTableData';
import { getPvmImpactExportData } from 'parser/graphToExcelDownloadParser';
import GrowthAnalysisTable from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { UserContext } from 'contexts/ConfigContext';
import PVMTrendChart2 from './PVMTrendChart2';
import { colorConstants } from 'config/colorConstants';
import { Tooltip } from 'antd';
import CopyToClipBoard, { classToRemoveScrollbar } from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import TileButton from 'components/atoms/TileButton';
import { CustomSelect } from 'CustomComponentCss';
import DownSvg from 'assets/svg/Down';
import { useFetchFeatureAttributeData } from 'hooks/pvmAnalysis';
import { FilterContextData } from 'contexts/FilterContext';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import _ from 'lodash';
import { tableFirstColumnHeader } from 'components/pages/PVMAnalysis';
import { ConfigKeys } from 'utils/Enums';

const yAxisGraphWidth = calculateFontSize(window.innerWidth, 60);
export const groupBarChartWidth = calculateFontSize(window.innerWidth, 100);
export const graphXAxisLabelFontSize = calculateFontSize(window.innerWidth, 13);
export const salesGraphWidth = calculateFontSize(window.innerWidth, 90);
export const barWidth = calculateFontSize(window.innerWidth, 31);

type Props = {
    commonVariables: any;
    attributeDropdownOptions: Array<any>;
    level1DrillDownItem: any;
    level2DrillDownItem: any;
    setLevel2DrillDownItem: Function;
    drillDownItem: any;
    exportTimePeriodHeader: any;
};
const PVMInfiniteImpactChartChartContainer = ({
    commonVariables: parentCommonVariables,
    attributeDropdownOptions,
    level1DrillDownItem,
    level2DrillDownItem,
    setLevel2DrillDownItem,
    drillDownItem,
    exportTimePeriodHeader,
}: Props) => {
    const { appConfig } = useContext(UserContext);
    const { idForRestOfComponent, allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);
    const pvmAnalysisConfig = getConfig('pvmAnalysis', appConfig);
    const { absTileData, dataSizeOptions } = pvmAnalysisConfig;
    const [selectedAttribute, setSelectedAttribute] = useState<any>(attributeDropdownOptions[3]); // 3 for Brand
    const [absTile, setAbsTile] = useState(absTileData[0]);
    const [selectedDataSize, setSelectedDataSize] = useState(dataSizeOptions[0]);
    const [intersectionFilter, setIntersectionFilter] = useState<any>(null);
    const pvmInfiniteChartRef = useRef(null);
    const refTable = useRef(null);
    const { decimalForNonPercentage, decimalForPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const [off, setOff] = useState(0);

    const offsetRef = useRef({
        offset: 0,
        width: 0,
    });
    useEffect(() => {
        setLevel2DrillDownItem(null);
        offsetRef.current.offset = 0;
        offsetRef.current.width = 0;
        setSelectedDataSize(dataSizeOptions[0]);
    }, [level1DrillDownItem, drillDownItem, selectedAttribute]);

    useEffect(() => {
        setOff(0);
        offsetRef.current.offset = 0;
    }, [selectedAttribute, selectedDataSize, absTile]);

    const { loading: applyFilterMutateDataLoading, applyFilterMutate: applyFilterDataMutate } = useApplyFilterMutation({
        onComplete: onApplyFilterComplete,
    });

    const commonVariables = useMemo(() => {
        return {
            ...parentCommonVariables,
            attribute_name: selectedAttribute?.value,
            data_size: selectedDataSize.value,
            dimension: selectedAttribute?.dim,
            limit: selectedDataSize?.limit,
            offset: offsetRef?.current?.offset || 0,
            data_type: absTile.key,
        };
    }, [absTile, selectedAttribute, selectedDataSize, parentCommonVariables, off]);

    // To get filterd data for drilldown table
    const tableVaribales = useMemo(() => {
        return {
            ...commonVariables,
            filters: intersectionFilter || {},
        };
    }, [intersectionFilter]);

    const {
        data: featureAttributeData,
        loading: featureAttributeLoading,
        error: featureAttributeError,
    } = useFetchFeatureAttributeData({
        queryKey: ['pvm-attribute-feature-data', commonVariables],
        variables: commonVariables,
        queryConfig: {
            // enabled: memoisedAttributeDropdownValues?.length > 0,
            cacheTime: 0,
        },
        offset: offsetRef?.current?.offset,
    });

    // Hooks for intersection/Drilldown data
    const {
        data: FeatureDrilldownTableData,
        loading: FeatureDrilldownTableLoading,
        error: FeatureDrilldownTableError,
    } = useFetchFeatureTableData({
        queryKey: ['pvm-feature-drilldown-table-data', tableVaribales],
        variables: {
            ...tableVaribales,
            feature_group_name: commonVariables.feature_group_name,
        },
        queryConfig: {
            enabled: !!level2DrillDownItem && !applyFilterMutateDataLoading && !!intersectionFilter,
        },
    });

    const memoisedTableColumns = useMemo(() => {
        let columns: any[] = [];
        if (FeatureDrilldownTableData?.length > 0) {
            const keyNames = Object.keys(FeatureDrilldownTableData[0]);
            columns = getPVMTableDataColumns(
                FeatureDrilldownTableData,
                tableFirstColumnHeader,
                decimalForNonPercentage,
                decimalForPercentage,
                pvmAnalysisConfig?.tableLabelMapping,
            );
        }
        return columns;
    }, [FeatureDrilldownTableData]);

    const tableHeaders = useMemo(() => {
        let labels: any[] = [''];
        if (FeatureDrilldownTableData?.length > 0) {
            labels = labels.concat(Object.keys(FeatureDrilldownTableData && FeatureDrilldownTableData[0]).slice(1));
        }
        return labels;
    }, [FeatureDrilldownTableData]);

    const infiniteScrollOnNext = (width: number, loading: boolean) => {
        if (loading) return;
        //  console.log('logging asd', width, loading);
        // infiniteScrollerNext(index, width);
        infiniteScrollerOnNext(width);
    };
    // Intersection/Drilldown table header
    const header = `${capitalizeFirstLetterOfEachWord(parentCommonVariables?.pvm_feature_name?.replace(/_/g, ' '))} ${
        level2DrillDownItem?.category
    } Sales Data Table`;

    //called when user infinite scrolls
    const infiniteScrollerOnNext = useCallback((width: number) => {
        offsetRef.current.width = width;
        const newOffsetFirst = offsetRef.current.offset + pvmAnalysisConfig.limit;
        offsetRef.current.offset = newOffsetFirst;
        //    to trigger api call
        setOff(newOffsetFirst);
    }, []);

    const containerWidth = useContainerWidth({
        containerId: `pvm-analysis-sales-infinite-scroll`,
        buffer: yAxisGraphWidth + 34 * 2,
    }); //subtracting graph width and padding on 2 sides

    const graphViewContainerWidth = Math.max(
        featureAttributeData?.data ? featureAttributeData.data?.length * salesGraphWidth : 0,
        containerWidth,
    );
    const perGraphComponentWidth = graphViewContainerWidth / featureAttributeData?.data?.length || 0;

    const labelHeader = useMemo(() => {
        return `${selectedDataSize.label} ${selectedAttribute.label} - ${
            level1DrillDownItem?.category || drillDownItem.category
        } `;
    }, [level1DrillDownItem, selectedAttribute, selectedDataSize, drillDownItem]);

    const currency = useGetCurrencyForTenant();
    let yAxisLabel: any = `Sales  ${absTile?.key === absTileData[0].key ? currency : '%'}`;

    function onApplyFilterComplete(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(idForRestOfComponent, payload);
        setIntersectionFilter(tempApplyFilterResponse);
    }

    const exportData = useMemo(() => {
        return getPvmImpactExportData(FeatureDrilldownTableData);
    }, [FeatureDrilldownTableData]);

    const defaultApplyFilter = {
        jsonstring_dim1: {},
        jsonstring_dim4: {},
    };

    useEffect(() => {
        if (level2DrillDownItem?.category) {
            const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
            let dim1Variables = {};
            let dim4Variables = {};
            if (selectedAttribute.dim === 'dim1') {
                dim1Variables = {
                    attributeExId: level2DrillDownItem.category,
                    attributeDesc: level2DrillDownItem.category,
                    attributeType: selectedAttribute.value,
                    dimension: 'dim1',
                };
                tempApplyFilterVariable.jsonstring_dim1.inclusion = [dim1Variables];
            } else {
                dim4Variables = {
                    attributeExId: level2DrillDownItem.category,
                    attributeDesc: level2DrillDownItem.category,
                    attributeType: selectedAttribute.value,
                    dimension: 'dim4',
                };
                tempApplyFilterVariable.jsonstring_dim4.inclusion = [dim4Variables];
            }

            tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
            tempApplyFilterVariable.jsonstring_dim4 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim4);
            applyFilterDataMutate({ variables: tempApplyFilterVariable });
        }
    }, [level2DrillDownItem, selectedAttribute]);

    // IF ERROR RETURN ERROR MESSAGE
    if (!!featureAttributeError)
        return (
            <div
                className={`${graphItemContainerClass}  mt-4  mb-0 flex-1 overflow-hidden`}
                style={{ minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <ErrorBox msg={String(featureAttributeError)} />
            </div>
        );

    // IF LOADING RETURN LOADER
    if (featureAttributeLoading)
        return (
            <div
                className={`${graphItemContainerClass} mt-4   mb-0 flex-1 overflow-hidden`}
                style={{ minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Loader />
            </div>
        );

    // ELSE RETURN COMPONENT
    return (
        <div style={{ minHeight: height }}>
            <div className={`flex flex-col mt-4 ${graphItemContainerClass}`} ref={pvmInfiniteChartRef}>
                <div className="flex justify-between items-center" style={{ marginTop: 14, marginBottom: 24 }}>
                    <div className="flex gap-2">
                        <span className={subHeaderClass}>{labelHeader} </span>
                        <CustomSelect
                            style={{ marginRight: 16 }}
                            suffixIcon={<DownSvg />}
                            disabled={false}
                            //loading={allDimAndAttributeTypeList?.loading}
                            value={selectedAttribute?.value}
                            options={attributeDropdownOptions}
                            className="dropdown-height dropdown-width-alt uniqSelPVMSalesOrderByBrandDropdown"
                            onChange={(val: any, item: any) => {
                                trackEventMixpanel(PVM_ANALYSIS_CATEGORY_DROP_DOWN_CLICKED, {
                                    PVMSalesChart: `${item.value}`,
                                });
                                setSelectedAttribute(item);
                                //mixpanel event
                                // trackEventMixpanel(GROWTH_ANALYSIS_UNIT_DROPDOWN_CHANGED, {
                                //     selectedDropdown: item?.label,
                                // });
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <CustomSelect
                            style={{ marginRight: 16 }}
                            suffixIcon={<DownSvg />}
                            disabled={false}
                            //loading={allDimAndAttributeTypeList?.loading}
                            value={selectedDataSize.value}
                            options={dataSizeOptions}
                            className="dropdown-height dropdown-width-alt uniqSelPVMSalesOrderByValueDropdown"
                            onChange={(val: any, item: any) => {
                                trackEventMixpanel(PVM_ANALYSIS_ORDER_BY_VALUE_CLICKED, {
                                    PVMSalesChart: `${item.value}`,
                                });
                                setSelectedDataSize(item);
                                setLevel2DrillDownItem(null);
                                //mixpanel event
                                // trackEventMixpanel(GROWTH_ANALYSIS_UNIT_DROPDOWN_CHANGED, {
                                //     selectedDropdown: item?.label,
                                // });
                            }}
                        />
                        <TileButton
                            data={absTileData}
                            activeKey={absTile.key}
                            onClick={(item: any) => {
                                setAbsTile(item);
                                trackEventMixpanel(PVM_ANALYSIS_ABSOLUTE_PERCENTAGE_TOGGLE_CLICKED, {
                                    PVMToggleButtonValue: item.label,
                                });
                            }}
                            // style={{ marginRight: 16 }}
                        />

                        <CopyToClipBoard
                            mixpanelEvent={() =>
                                trackEventMixpanel(PVM_ANALYSIS_COPY_SCREENSHOT, {
                                    PVMSalesBarChart: `${labelHeader}`,
                                })
                            }
                            refOfComponent={pvmInfiniteChartRef}
                            removeScrollbar
                        />
                        <DownloadExcel
                            customTimePeriodHeader={exportTimePeriodHeader}
                            title={labelHeader}
                            mixpanelEvent={() =>
                                trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                    PVMSalesBarChart: `${labelHeader}`,
                                })
                            }
                            excelData={[
                                ...(featureAttributeData?.data?.map((item: any) => [item.category, item.sales]) || []),
                            ]}
                            headers={[selectedAttribute?.label, 'Sales']}
                        />
                    </div>
                </div>

                {featureAttributeData && (
                    <div className="mb-4 flex justify-between items-center">
                        <p className={`${yAxisLabelClass} w-[120px]`}>{yAxisLabel}</p>
                        <Legend data={legendData} />
                        <p className="w-[120px] font-normal text-greyFont font-xxs"> </p>
                    </div>
                )}

                <div id="pvmAnalysisInfiniteSalesChart" className="flex ml-1 w-full">
                    <div style={{ minWidth: yAxisGraphWidth }}>
                        <Barchart
                            colorKey="color"
                            dataset={[]}
                            height={height}
                            loading={false}
                            chartContainer={`SalesY-2`}
                            fields={['category', 'sales']}
                            minBound={scaleMinValue(105, featureAttributeData?.minVal)}
                            maxBound={scaleMaxValue(80, featureAttributeData?.maxVal)}
                            width={yAxisGraphWidth}
                            YGrid={false}
                            padding={[0, 0, 0, 0]}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                            }}
                            xAxisConfig={false}
                        />
                    </div>
                    <HorizontalInfiniteScroller
                        id={`pvm-analysis-sales-infinite-scroll`}
                        hasMore={featureAttributeData?.data?.length < featureAttributeData?.count || false} //subtracting 1 to remove total value which is the first item
                        scrollThreshold={0.9}
                        toScrollWidth={offsetRef.current.width}
                        onNext={(width: number) => {
                            infiniteScrollOnNext(width, featureAttributeLoading);
                        }}
                    >
                        <div
                            id={`pvm-analysis-sales-infinite-scroll`}
                            className={`flex pb-[5px] ${classToRemoveScrollbar}`}
                            style={{ maxWidth: '100%', overflowX: 'auto' }}
                        >
                            {featureAttributeData &&
                                'data' in featureAttributeData &&
                                Array.isArray(featureAttributeData.data) &&
                                featureAttributeData?.data.map((attrData: any, idx: number) => {
                                    const item = attrData;

                                    const isItemSelected = attrData?.category === level2DrillDownItem?.category;

                                    //if idx===0 it means showing total, make total bar sticky so that it doesnt move with scroll
                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                position: 'static',
                                                left: 'auto',
                                                background: 'transparent',
                                                zIndex: 'auto',
                                            }}
                                        >
                                            <div
                                                onClick={() => {
                                                    trackEventMixpanel(PVM_ANALYSIS_DRILL_DOWN_DIM_VALUE_SELECTED, {
                                                        PVMSelectedDimName: `${selectedAttribute.value}`,
                                                        PVMSelectedDimValueName: `${attrData?.category}`,
                                                    });
                                                    setLevel2DrillDownItem(
                                                        attrData?.category === level2DrillDownItem?.category
                                                            ? null
                                                            : attrData,
                                                    );
                                                }}
                                                style={{
                                                    position: 'relative',
                                                }}
                                                className={`${
                                                    isItemSelected ? 'bg-bgBackground' : 'hover:bg-greyBackgroundAlt'
                                                } group`}
                                            >
                                                <div style={{ height: height }}>
                                                    <Barchart
                                                        barWidth={barWidth}
                                                        dataset={[item]}
                                                        height={height}
                                                        loading={false}
                                                        chartContainer={`SalesX-${idx}`}
                                                        fields={['category', 'sales']}
                                                        intervalLabelTooltipKey={'labelSales'}
                                                        minBound={scaleMinValue(105, featureAttributeData?.minVal)}
                                                        maxBound={scaleMaxValue(80, featureAttributeData?.maxVal)}
                                                        width={perGraphComponentWidth || salesGraphWidth}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: xAxis,
                                                                value:
                                                                    absTile.key === 'abs'
                                                                        ? formatNumWithKMBAlt(
                                                                              val,
                                                                              decimalForNonPercentage,
                                                                              decimalForNonPercentage,
                                                                          )
                                                                        : formatNumWithKMBAlt(
                                                                              val,
                                                                              decimalForPercentage,
                                                                          ) + '%',
                                                            };
                                                        }}
                                                        intervalFormatter={(val: any) => {
                                                            return absTile.key === 'abs'
                                                                ? formatNumWithKMBAlt(
                                                                      val,
                                                                      decimalForNonPercentage,
                                                                      decimalForNonPercentage,
                                                                  )
                                                                : formatNumWithKMBAlt(val, decimalForPercentage) + '%';
                                                        }}
                                                        colorKey={'color'}
                                                    />
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    width: perGraphComponentWidth || salesGraphWidth,
                                                    fontSize: graphXAxisLabelFontSize,
                                                }}
                                                className="text-center p-[2px] "
                                            >
                                                {item?.category}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </HorizontalInfiniteScroller>
                </div>
                <p className={`text-center relative ${yAxisLabelClass} `}>{selectedAttribute.label} </p>
            </div>

            {level2DrillDownItem && (
                <div className="mt-2">
                    <PVMTrendChart2
                        exportTimePeriodHeader={exportTimePeriodHeader}
                        level2DrillDownItem={level2DrillDownItem}
                        selectedAttribute={selectedAttribute}
                        commonVariables={parentCommonVariables}
                        applyFilterMutateDataLoading={applyFilterMutateDataLoading}
                        trendsChartFilters={intersectionFilter}
                        decimalForNonPercentage={decimalForNonPercentage}
                        decimalForPercentage={decimalForPercentage}
                    />
                    <div className="flex justify-between items-center mt-2 mb-1">
                        <p className={`${subHeaderClass} mb-2`}>{header}</p>
                        <div className="flex justify-end mb-2 mt-4">
                            <span className="flex gap-2">
                                <CopyToClipBoard
                                    mixpanelEvent={() =>
                                        trackEventMixpanel(PVM_ANALYSIS_COPY_SCREENSHOT, {
                                            PVMGlobalDataTable: `${header}`,
                                        })
                                    }
                                    refOfComponent={refTable}
                                />
                                <DownloadExcel
                                    customTimePeriodHeader={exportTimePeriodHeader}
                                    title={`${level1DrillDownItem?.category} Data Table`}
                                    mixpanelEvent={() =>
                                        trackEventMixpanel(PVM_ANALYSIS_DOWNLOAD_DATA, {
                                            PVMGlobalDataTable: `${header}`,
                                        })
                                    }
                                    excelData={exportData.result}
                                    headers={exportData.headers}
                                />
                            </span>
                        </div>
                    </div>
                    {/* Added Intersection Data Table  */}
                    <div ref={refTable}>
                        <GrowthAnalysisTable
                            columns={memoisedTableColumns}
                            data={FeatureDrilldownTableData || []}
                            tableLoading={applyFilterMutateDataLoading || FeatureDrilldownTableLoading}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PVMInfiniteImpactChartChartContainer;
